/*
 * Glossary tooltip UX: make <details class="glossary-tooltip"> behave like a
 * normal popover.
 *
 *  1. On devices with a real hover-capable pointer (desktop mice, trackpads,
 *     etc.), the tooltip opens on hover and stays open while the cursor is
 *     over the trigger OR the bubble. Leaving both starts a short grace
 *     timer (150ms) before dismissing — that window is enough to let the
 *     cursor transit the small visual gap between the `?` trigger and the
 *     bubble without the bubble snapping shut. Re-entering cancels the
 *     timer.
 *
 *  2. Clicking the `?` trigger PINS the tooltip open. Pinned tooltips are
 *     immune to mouseleave (no grace-timer countdown), so the user can move
 *     the cursor freely while reading or clicking the "Read more" link.
 *     Clicking outside any open tooltip, or pressing Escape, clears all
 *     pins and closes everything. Clicking the trigger of an already-pinned
 *     tooltip closes it (toggle).
 *
 *  3. Only one tooltip is ever open at a time — opening one closes the rest.
 *
 *  4. On touch / no-hover devices, the hover handlers are not attached at
 *     all. Click-to-toggle remains the only interaction model, which is the
 *     correct UX for touch.
 *
 *  5. Press Escape closes every open tooltip.
 *
 * No frameworks, no dependencies. Loaded once per page via main-product.liquid
 * (product page) and filter-glossary-helper.liquid (collection page "Coffee
 * terms — quick reference" panel). Safe to load multiple times: the
 * listeners are idempotent — they all hang off `document` and are gated by a
 * global flag.
 */
(function () {
  if (window.__glossaryTooltipInit) return;
  window.__glossaryTooltipInit = true;

  // Hover-to-open is gated behind a `(hover: hover)` media query so it
  // never activates on touch devices (where a naive `:hover` would fire on
  // tap-then-tap-elsewhere and feel broken). `pointer: fine` narrows
  // further to precise pointers — mice, trackpads — excluding coarse
  // touch surfaces even on hybrid devices.
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Centralized close so every dismissal path (click-outside, Escape,
  // sibling-opened, grace-timer fired, click-to-toggle) cleans up the
  // tooltip's auxiliary state (openMode tag + any pending close timer).
  function closeTip(tip) {
    tip.removeAttribute('open');
    delete tip.dataset.openMode;
    if (tip._closeTimer) {
      clearTimeout(tip._closeTimer);
      tip._closeTimer = null;
    }
  }

  function closeAllExcept(except) {
    document
      .querySelectorAll('details.glossary-tooltip[open]')
      .forEach(function (d) {
        if (d !== except) closeTip(d);
      });
  }

  // -- Click: toggle + pin ------------------------------------------------
  //
  // The native <details> element toggles `open` on click of <summary>.
  // We extend that with a `dataset.openMode` tag so hover-opened and
  // click-opened tooltips can be told apart, plus a preventDefault path
  // for the "currently hover-open, user clicks to pin" transition where
  // the native toggle would otherwise close it.
  //
  // State transitions on trigger click:
  //   closed                → opens, openMode = 'click' (pinned)
  //   open via hover        → preventDefault, openMode upgraded to 'click'
  //   open via click (pin)  → closes, openMode cleared
  document.addEventListener('click', function (event) {
    var summary = event.target.closest(
      'details.glossary-tooltip > summary.glossary-tooltip__trigger'
    );

    if (summary) {
      var tip = summary.parentElement;

      // Opening any tooltip implicitly dismisses the others.
      closeAllExcept(tip);

      // If this tooltip had a hover-close timer queued, cancel it — the
      // user's explicit click should take precedence over the auto-dismiss.
      if (tip._closeTimer) {
        clearTimeout(tip._closeTimer);
        tip._closeTimer = null;
      }

      if (tip.hasAttribute('open')) {
        if (tip.dataset.openMode === 'hover') {
          // Hover-opened then clicked: pin instead of letting the native
          // toggle close it. Without preventDefault here, the click would
          // flip `open` off and the user would think the click broke it.
          event.preventDefault();
          tip.dataset.openMode = 'click';
        } else {
          // Already pinned and trigger clicked again: native toggle will
          // close it after this handler returns. Just clear the pin tag.
          delete tip.dataset.openMode;
        }
      } else {
        // Closed: native toggle will open it. Mark it pinned so a
        // subsequent mouseleave doesn't auto-dismiss.
        tip.dataset.openMode = 'click';
      }
      return;
    }

    // Click landed somewhere that ISN'T inside any open tooltip — dismiss
    // all of them.
    if (!event.target.closest('details.glossary-tooltip[open]')) {
      closeAllExcept(null);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllExcept(null);
  });

  // -- Hover (precise-pointer devices only) -------------------------------
  //
  // The hover region is the entire <details> element, which contains both
  // the <summary> trigger and the absolutely-positioned bubble. mouseover
  // bubbles, so event delegation on `document` correctly catches any
  // descendant. The grace timer on mouseout gives the cursor ~150ms to
  // transit the visual gap between the `?` icon and the bubble below it
  // (the bubble sits at `top: calc(100% + 0.6rem)`, so there's roughly 6px
  // of dead space the cursor has to cross — well within 150ms at any
  // normal pointer speed).
  if (canHover) {
    document.addEventListener('mouseover', function (event) {
      var tip = event.target.closest('details.glossary-tooltip');
      if (!tip) return;

      // Re-entered before the close timer fired → cancel the dismissal.
      if (tip._closeTimer) {
        clearTimeout(tip._closeTimer);
        tip._closeTimer = null;
      }

      if (!tip.hasAttribute('open')) {
        closeAllExcept(tip);
        tip.setAttribute('open', '');
        tip.dataset.openMode = 'hover';
      }
    });

    document.addEventListener('mouseout', function (event) {
      var tip = event.target.closest('details.glossary-tooltip');
      if (!tip || !tip.hasAttribute('open')) return;

      // Pinned tooltips never auto-close on mouseleave.
      if (tip.dataset.openMode === 'click') return;

      // mouseout fires on every internal element transition too (e.g. when
      // the cursor moves from the bubble's <p> to its <a>). Skip those:
      // if the cursor's destination (relatedTarget) is still inside this
      // tooltip, we're not actually leaving.
      if (event.relatedTarget && tip.contains(event.relatedTarget)) return;

      tip._closeTimer = setTimeout(function () {
        tip.removeAttribute('open');
        delete tip.dataset.openMode;
        tip._closeTimer = null;
      }, 150);
    });
  }
})();
